import React, { useEffect } from 'react'
import ReactGA from 'react-ga4'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import Header from '../components/Header'
import Footer from '../components/Footer'
// Optimized WebP images
import collinImage from '../assets/optimized/collin.webp'
import sabeekImage from '../assets/optimized/sabeek.webp'
import eddyImage from '../assets/optimized/eddy.webp'
import mattImage from '../assets/optimized/matt.webp'
import googleLogo from '../assets/optimized/google-logo.webp'
import stanfordLogo from '../assets/optimized/stanford-logo.webp'
// SVG and other logos (no need to optimize SVGs)
import verilyLogo from '../assets/verily-light.png'
import zooxLogo from '../assets/zoox.png'
import waymoLogo from '../assets/waymo.svg'
import appliedIntuitionLogo from '../assets/applied-intuition.svg'
import nxpLogo from '../assets/nxp.svg'
import auroraSolarLogo from '../assets/aurora-white.svg'
import ycLogo from '../assets/yc-white.svg'
import nvidiaLogo from '../assets/nvidia-inception-program-badge-rgb-for-screen.svg'
import '../main.css'

const Team = () => {
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname })
  }, [])

  return (
    <div className='page-content'>
      <div className='shapy-background'>
        <Header />
        <div className='team-container'>
          <div className='team-member-box'>
            <LazyLoadImage
              src={collinImage}
              alt='Collin Barnwell'
              effect="blur"
              threshold={100}
              wrapperClassName="team-member-image-wrapper"
              style={{ width: '100%', height: 'auto' }}
            />
            <div className='team-member-content'>
              <h2>
                <a
                  href='https://www.linkedin.com/in/collinbarnwell/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Collin Barnwell
                </a>
              </h2>
              <h3>CEO</h3>
              <p>
                Collin is a software leader and engineer with extensive
                experience building and shipping safety-critical IoT and camera
                devices, including a device that has been implanted in humans.
                He's worked at Google and Verily Life Sciences.
              </p>
              <div className='company-logos'>
                <LazyLoadImage src={googleLogo} alt='Google' className='company-logo' effect="blur" threshold={100} />
                <LazyLoadImage src={verilyLogo} alt='Verily' className='company-logo' effect="blur" threshold={100} />
              </div>
            </div>
          </div>
          <div className='team-member-box'>
            <LazyLoadImage
              src={sabeekImage}
              alt='Sabeek Pradhan'
              effect="blur"
              threshold={100}
              wrapperClassName="team-member-image-wrapper"
              style={{ width: '100%', height: 'auto' }}
            />
            <div className='team-member-content'>
              <h2>
                <a
                  href='https://www.linkedin.com/in/sabeek-pradhan/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Sabeek Pradhan
                </a>
              </h2>
              <h3>CTO</h3>
              <p>
                Sabeek is a computer vision expert with extensive experience
                building systems for high-accuracy vehicle, pedestrian and bike
                detection and tracking at the self-driving car companies Zoox,
                Waymo and Applied Intuition, and his software has been deployed
                on public roads in fully driverless vehicles.
              </p>
              <div className='company-logos'>
                <img
                  src={stanfordLogo}
                  alt='Stanford'
                  className='company-logo'
                />
                <LazyLoadImage
                  src={zooxLogo}
                  alt='Zoox'
                  className='company-logo inverted-logo'
                  effect="blur"
                  threshold={100}
                />
                <LazyLoadImage src={waymoLogo} alt='Waymo' className='company-logo' effect="blur" threshold={100} />
                <LazyLoadImage
                  src={appliedIntuitionLogo}
                  alt='Applied Intuition'
                  className='company-logo inverted-logo'
                  effect="blur"
                  threshold={100}
                />
              </div>
            </div>
          </div>
          <div className='team-member-box'>
            <LazyLoadImage
              src={eddyImage}
              alt='Eduard Strehlau'
              effect="blur"
              threshold={100}
              wrapperClassName="team-member-image-wrapper"
              style={{ width: '100%', height: 'auto' }}
            />
            <div className='team-member-content'>
              <h2>
                <a
                  href='https://www.linkedin.com/in/eduard-strehlau/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Eduard Strehlau
                </a>
              </h2>
              <h3>Embedded Software Engineer</h3>
              <p>
                Eduard is an embedded software engineer specializing in
                low-level systems programming and hardware integrations and IoT.
                He's shipped devices for a wide range of applications and use
                cases as a consultant and prior to that worked at NXP.
              </p>
              <div className='company-logos'>
                <LazyLoadImage src={nxpLogo} alt='NXP' className='company-logo' effect="blur" threshold={100} />
              </div>
            </div>
          </div>
          <div className='team-member-box'>
            <LazyLoadImage
              src={mattImage}
              alt='Matt Stevens'
              effect="blur"
              threshold={100}
              wrapperClassName="team-member-image-wrapper"
              style={{ width: '100%', height: 'auto' }}
            />
            <div className='team-member-content'>
              <h2>
                <a
                  href='https://www.linkedin.com/in/matt-stevens-11a27aab/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Matt Stevens
                </a>
              </h2>
              <h3>Machine Learning Engineer</h3>
              <p>
                Matt is a machine learning engineer with a deep expertise
                developing and deploying computer vision models from his time at
                Aurora solar and did a project exploring the use of AI to
                optimize traffic networks while at Stanford.
              </p>
              <div className='company-logos'>
                <img
                  src={stanfordLogo}
                  alt='Stanford'
                  className='company-logo'
                />
                <LazyLoadImage
                  src={auroraSolarLogo}
                  alt='Aurora Solar'
                  className='company-logo aurora-logo'
                  effect="blur"
                  threshold={100}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='partner-logos'>
          <LazyLoadImage
            src={ycLogo}
            alt='Y Combinator Company'
            style={{ height: '100px', maxWidth: '100%', margin: '10px' }}
            effect="blur"
            threshold={100}
          />
          <LazyLoadImage
            src={nvidiaLogo}
            alt='NVIDIA Inception Program Member'
            style={{ height: '100px', maxWidth: '100%', margin: '10px' }}
            effect="blur"
            threshold={100}
          />
        </div>
        <Footer />
      </div>
    </div>
  )
}

export default Team
